/* ══════════════════════════════════════════════════════════════════════
   Diagram components for the deck.

   Deck holds the SN beige (sn-section-beige) theme throughout.
   `tone="light"` is the default; "dark" remains for future re-use.

   Visual grammar:
     • White cards with subtle borders
     • Blue accent (sn-blue-5) for stroke / fill
     • Dashed paths animated via stroke-dashoffset (data in motion)
     • Explicit arrowheads on terminal segments
   ══════════════════════════════════════════════════════════════════════ */

import { type ReactNode } from 'react';

export type Glyph =
  | 'db' | 'doc' | 'vector' | 'olap' | 'feature'
  | 'agent' | 'tool' | 'gateway' | 'log' | 'branch'
  | 'rules' | 'cache' | 'router' | 'audit' | 'replay'
  | 'mcp' | 'image' | 'audio' | 'crm' | 'lake' | 'stream'
  | 'llm' | 'function' | 'flask' | 'wrench';

export type FlowNode = { label: string; sub?: string; glyph?: Glyph };

type Tone = 'light' | 'dark';

const C = {
  inkLight: '#202939',
  inkDark: '#FFFFFF',
  muteLight: '#4B5565',
  muteDark: '#B9D7FA',
  faintLight: '#697586',  // grey-5 — stronger than grey-4 for visible dashes
  faintDark: 'rgba(255,255,255,0.35)',
  cardBg: '#FFFFFF',
  cardBorderDark: 'rgba(255,255,255,0.5)',
  cardBorderLight: '#D7CDC3',  // grey-3 — slightly stronger
  accent: '#0070D9',
  accentBright: '#74B0F4',
  partner: '#365463',
} as const;

const pal = (tone: Tone) => ({
  ink: tone === 'dark' ? C.inkDark : C.inkLight,
  mute: tone === 'dark' ? C.muteDark : C.muteLight,
  faint: tone === 'dark' ? C.faintDark : C.faintLight,
  cardBg: C.cardBg,
  cardBorder: tone === 'dark' ? C.cardBorderDark : C.cardBorderLight,
  accent: tone === 'dark' ? C.accentBright : C.accent,
  centerFill: tone === 'dark' ? 'rgba(255,255,255,0.06)' : '#FFFFFF',
});

/* ══════════════════════════════════════════════════════════════════════
   LinearFlow — left-to-right pipeline with arrowheads
   ══════════════════════════════════════════════════════════════════════ */

export function LinearFlow({
  left, center, right, tone = 'light', leftLabel, centerLabel, rightLabel,
}: {
  left: readonly FlowNode[];
  center: { label: string; sub?: string; glyph?: Glyph };
  right: readonly FlowNode[];
  tone?: Tone;
  leftLabel?: string;
  centerLabel?: string;
  rightLabel?: string;
}) {
  const p = pal(tone);
  const W = 1200; const H = 540;
  const colXLeft = 130;
  const colXRight = W - 130;
  const centerX = W / 2;
  const centerY = H / 2 - 20;
  const stageWidth = 240;
  const stageHeight = 150;
  const markerId = `arrow-lf-${tone}`;

  const colY = (count: number, i: number) => {
    const top = 100; const bottom = H - 130;
    const step = count > 1 ? (bottom - top) / (count - 1) : 0;
    return top + i * step;
  };

  return (
    <svg className="flow-diagram" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={center.label}>
      <defs>
        <marker id={markerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={p.accent} />
        </marker>
      </defs>

      {/* left → center (paths converge to the middle of the stage's left boundary) */}
      {left.map((_, i) => {
        const y = colY(left.length, i);
        const startX = colXLeft + 36;
        const endX = centerX - stageWidth / 2;
        return (
          <path
            key={`l${i}`}
            d={`M ${startX} ${y} C ${(startX + endX) / 2} ${y}, ${(startX + endX) / 2} ${centerY}, ${endX} ${centerY}`}
            stroke={p.faint} strokeWidth={1.5} fill="none"
            strokeDasharray="3 6"
            className="flow-line"
            style={{ animationDelay: `${i * 200}ms` }}
            markerEnd={`url(#${markerId})`}
          />
        );
      })}

      {/* center → right (path fans out from stage right edge to each right card) */}
      {right.map((_, i) => {
        const y = colY(right.length, i);
        const startX = centerX + stageWidth / 2;
        const endX = colXRight - 36;
        return (
          <path
            key={`r${i}`}
            d={`M ${startX} ${centerY} C ${(startX + endX) / 2} ${centerY}, ${(startX + endX) / 2} ${y}, ${endX} ${y}`}
            stroke={p.faint} strokeWidth={1.5} fill="none"
            strokeDasharray="3 6"
            className="flow-line"
            style={{ animationDelay: `${i * 200 + 100}ms` }}
            markerEnd={`url(#${markerId})`}
          />
        );
      })}

      {/* center stage */}
      <g>
        <rect
          x={centerX - stageWidth / 2} y={centerY - stageHeight / 2}
          width={stageWidth} height={stageHeight}
          rx={12} ry={12}
          fill={p.centerFill}
          stroke={p.accent} strokeWidth={1.5}
        />
        {center.glyph && (
          <g transform={`translate(${centerX - 24}, ${centerY - 52})`}>
            <Glyph kind={center.glyph} size={48} accent={p.accent} />
          </g>
        )}
        <text x={centerX} y={centerY + 8} fill={p.ink} textAnchor="middle" className="flow-center-label">
          {center.label}
        </text>
        {center.sub && (
          <text x={centerX} y={centerY + 32} fill={p.mute} textAnchor="middle" className="flow-center-sub">
            {center.sub}
          </text>
        )}
      </g>

      {leftLabel && <text x={colXLeft - 60} y={H - 70} fill={p.mute} textAnchor="start" className="flow-side-label">{leftLabel}</text>}
      {rightLabel && <text x={colXRight + 60} y={H - 70} fill={p.mute} textAnchor="end" className="flow-side-label">{rightLabel}</text>}
      {centerLabel && <text x={centerX} y={centerY + stageHeight / 2 + 32} fill={p.mute} textAnchor="middle" className="flow-side-label">{centerLabel}</text>}

      {left.map((n, i) => (
        <NodeCard key={`ln${i}`} node={n} x={colXLeft} y={colY(left.length, i)} side="left" tone={tone} />
      ))}
      {right.map((n, i) => (
        <NodeCard key={`rn${i}`} node={n} x={colXRight} y={colY(right.length, i)} side="right" tone={tone} />
      ))}
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   LinearFlowGov — like LinearFlow + a governance arc beneath the center
   stage. Used for the Governance challenge slide.
   ══════════════════════════════════════════════════════════════════════ */

export function LinearFlowGov(props: Parameters<typeof LinearFlow>[0] & { arc: { label: string; stops: readonly string[] } }) {
  const { arc, ...rest } = props;
  const tone = rest.tone ?? 'light';
  const p = pal(tone);
  const W = 1200; const H = 540;
  const centerX = W / 2;
  const centerY = H / 2 - 20;
  const stageHeight = 150;

  return (
    <div className="linearflow-stack">
      <LinearFlow {...rest} />
      <svg
        className="flow-diagram gov-overlay"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        <GovernanceArc cx={centerX} cy={centerY + stageHeight / 2 + 10} width={W * 0.5} label={arc.label} stops={arc.stops} tone={tone} pal={p} />
      </svg>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   FlowDiagram — curved hub with arrowheads
   ══════════════════════════════════════════════════════════════════════ */

export type FlowDiagramProps = {
  left?: readonly FlowNode[];
  leftLabel?: string;
  right?: readonly FlowNode[];
  rightLabel?: string;
  center: { label: string; sub?: string; glyph?: Glyph };
  arc?: { label: string; stops: readonly string[] };
  tone?: Tone;
};

export function FlowDiagram({
  left = [], leftLabel, right = [], rightLabel, center, arc, tone = 'light',
}: FlowDiagramProps) {
  const p = pal(tone);
  const W = 1200; const H = 540;
  const cx = W / 2; const cy = H / 2 - 30; const r = 96;
  const markerId = `arrow-fd-${tone}`;

  const layoutColumn = (count: number, xPos: number) => {
    const top = 60; const bottom = H - 160;
    const step = count > 1 ? (bottom - top) / (count - 1) : 0;
    return Array.from({ length: count }, (_, i) => ({ x: xPos, y: top + i * step }));
  };
  const L = layoutColumn(left.length, 130);
  const R = layoutColumn(right.length, W - 130);

  const pathLeftToCenter = (from: { x: number; y: number }) => {
    const targetX = cx - r;
    const targetY = cy;
    const c1x = (from.x + targetX) / 2;
    const c2x = (from.x + targetX) / 2;
    return `M ${from.x + 36} ${from.y} C ${c1x} ${from.y}, ${c2x} ${targetY}, ${targetX} ${targetY}`;
  };
  const pathCenterToRight = (to: { x: number; y: number }) => {
    const startX = cx + r;
    const startY = cy;
    const c1x = (startX + to.x) / 2;
    const c2x = (startX + to.x) / 2;
    return `M ${startX} ${startY} C ${c1x} ${startY}, ${c2x} ${to.y}, ${to.x - 36} ${to.y}`;
  };

  return (
    <svg className="flow-diagram" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={center.label}>
      <defs>
        <marker id={markerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={p.accent} />
        </marker>
      </defs>
      <g className="flow-lines">
        {L.map((n, i) => (
          <path
            key={`l${i}`} d={pathLeftToCenter(n)}
            stroke={p.faint} strokeWidth={1.5} fill="none" strokeDasharray="3 6"
            className="flow-line" style={{ animationDelay: `${i * 200}ms` }}
            markerEnd={`url(#${markerId})`}
          />
        ))}
        {R.map((n, i) => (
          <path
            key={`r${i}`} d={pathCenterToRight(n)}
            stroke={p.faint} strokeWidth={1.5} fill="none" strokeDasharray="3 6"
            className="flow-line" style={{ animationDelay: `${i * 200 + 100}ms` }}
            markerEnd={`url(#${markerId})`}
          />
        ))}
      </g>
      <g>
        <circle cx={cx} cy={cy} r={r + 8} fill="none" stroke={p.faint} strokeWidth={1} opacity={0.45} />
        <circle cx={cx} cy={cy} r={r} fill={p.centerFill} stroke={p.accent} strokeWidth={1.5} />
        {center.glyph && (
          <g transform={`translate(${cx - 22}, ${cy - 38})`}>
            <Glyph kind={center.glyph} size={44} accent={p.accent} />
          </g>
        )}
        <text x={cx} y={cy + (center.glyph ? 22 : 4)} fill={p.ink} textAnchor="middle" className="flow-center-label">{center.label}</text>
        {center.sub && (
          <text x={cx} y={cy + (center.glyph ? 44 : 26)} fill={p.mute} textAnchor="middle" className="flow-center-sub">{center.sub}</text>
        )}
      </g>
      {leftLabel && <text x={210} y={cy + 4} fill={p.mute} className="flow-side-label" textAnchor="start">{leftLabel}</text>}
      {rightLabel && <text x={W - 210} y={cy + 4} fill={p.mute} className="flow-side-label" textAnchor="end">{rightLabel}</text>}
      {left.map((n, i) => <NodeCard key={`ln${i}`} node={n} x={L[i].x} y={L[i].y} side="left" tone={tone} />)}
      {right.map((n, i) => <NodeCard key={`rn${i}`} node={n} x={R[i].x} y={R[i].y} side="right" tone={tone} />)}
      {arc && <GovernanceArc cx={cx} cy={cy + r + 30} width={W * 0.55} label={arc.label} stops={arc.stops} tone={tone} pal={p} />}
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   AuditTeeDiagram — REWORKED
   Agent on the left. LLM + Tool stacked on the right. Round-trip arcs
   live within the band between Agent and target — never crossing other
   objects. Tees drop straight down to a single horizontal audit log;
   arrow continues to the object store on the right.
   ══════════════════════════════════════════════════════════════════════ */

export function AuditTeeDiagram({ tone = 'light' }: { tone?: Tone }) {
  const p = pal(tone);
  const W = 1200; const H = 560;
  const markerId = `arrow-audit-${tone}`;

  // Separate LLM and Tool on BOTH x and y axes so arcs cannot overlap.
  const agentX = 180;
  const agentY = 230;
  const llmX = 560;   const llmY = 110;
  const toolX = 880;  const toolY = 320;

  const barX0 = 110;
  const barX1 = W - 240;
  const barY = 460;

  const storeX = W - 130;
  const storeY = barY;

  // round-trip helpers
  const upperArc = (sx: number, sy: number, ex: number, ey: number) => {
    const cx = (sx + ex) / 2;
    const cy = Math.min(sy, ey) - 28;
    return `M ${sx} ${sy} Q ${cx} ${cy}, ${ex} ${ey}`;
  };
  const lowerArc = (sx: number, sy: number, ex: number, ey: number) => {
    const cx = (sx + ex) / 2;
    const cy = Math.max(sy, ey) + 28;
    return `M ${sx} ${sy} Q ${cx} ${cy}, ${ex} ${ey}`;
  };

  return (
    <svg className="flow-diagram audit" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Audit-tee diagram">
      <defs>
        <marker id={markerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={p.accent} />
        </marker>
      </defs>

      {/* agent ↔ llm — upper-half arcs */}
      <path d={upperArc(agentX + 38, agentY - 10, llmX - 38, llmY + 10)} stroke={p.accent} strokeWidth={1.5} fill="none" strokeDasharray="4 6" className="flow-line" markerEnd={`url(#${markerId})`} />
      <path d={lowerArc(llmX - 38, llmY + 18, agentX + 38, agentY - 4)} stroke={p.accent} strokeWidth={1.5} fill="none" strokeDasharray="4 6" className="flow-line" style={{ animationDelay: '120ms' }} markerEnd={`url(#${markerId})`} />

      {/* agent ↔ tool — lower-half arcs */}
      <path d={lowerArc(agentX + 38, agentY + 10, toolX - 38, toolY - 10)} stroke={p.accent} strokeWidth={1.5} fill="none" strokeDasharray="4 6" className="flow-line" style={{ animationDelay: '200ms' }} markerEnd={`url(#${markerId})`} />
      <path d={upperArc(toolX - 38, toolY - 18, agentX + 38, agentY + 4)} stroke={p.accent} strokeWidth={1.5} fill="none" strokeDasharray="4 6" className="flow-line" style={{ animationDelay: '320ms' }} markerEnd={`url(#${markerId})`} />

      {/* nodes (drawn after arcs so they sit on top) */}
      <NodeCard node={{ label: 'Agent', sub: 'plan · act', glyph: 'agent' }} x={agentX} y={agentY} side="right" tone={tone} large />
      <NodeCard node={{ label: 'LLM call', sub: 'reasoning', glyph: 'llm' }} x={llmX} y={llmY} side="right" tone={tone} large />
      <NodeCard node={{ label: 'Tool call', sub: 'side effects', glyph: 'wrench' }} x={toolX} y={toolY} side="right" tone={tone} large />

      {/* tees: agent and both targets drop straight down to the audit bar */}
      {[agentX, llmX, toolX].map((x, i) => {
        const sourceY = i === 0 ? agentY + 38 : (i === 1 ? llmY + 38 : toolY + 38);
        return (
          <path
            key={`tee${i}`}
            d={`M ${x} ${sourceY} L ${x} ${barY - 30}`}
            stroke={p.faint} strokeWidth={1.25} strokeDasharray="3 6"
            className="flow-line"
            style={{ animationDelay: `${i * 150 + 400}ms` }}
          />
        );
      })}

      {/* audit log bar */}
      <rect
        x={barX0} y={barY - 32}
        width={barX1 - barX0} height={64}
        rx={12} ry={12}
        fill={p.centerFill}
        stroke={p.accent} strokeWidth={1.5}
      />
      <text x={barX0 + 24} y={barY + 4} fill={p.ink} className="flow-center-label">Audit log</text>
      <text x={barX0 + 24} y={barY + 24} fill={p.mute} className="flow-center-sub">streaming · full payloads · append-only</text>
      {Array.from({ length: 18 }).map((_, i) => (
        <rect
          key={i}
          x={barX0 + 260 + i * 30}
          y={barY - 10}
          width={3} height={20} rx={1}
          fill={p.accent}
          opacity={0.4 + (i % 3) * 0.15}
        />
      ))}

      {/* audit log → object store */}
      <path
        d={`M ${barX1} ${barY} L ${storeX - 42} ${storeY}`}
        stroke={p.accent} strokeWidth={1.5}
        markerEnd={`url(#${markerId})`}
      />
      <NodeCard node={{ label: 'Object store', sub: 'tiered', glyph: 'db' }} x={storeX} y={storeY} side="left" tone={tone} large />

      <text x={W / 2} y={H - 20} fill={p.mute} textAnchor="middle" className="flow-side-label">
        Streaming serves the audit log · full I/O capture · auditor-ready
      </text>
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   HypothesisBranches — append-only log with branches
   ══════════════════════════════════════════════════════════════════════ */

export function HypothesisBranches({ tone = 'light' }: { tone?: Tone }) {
  const p = pal(tone);
  const W = 1200; const H = 440;
  const trunkY = 260;

  const trunkEvents = [120, 220, 320, 420, 520, 620];
  const branchOriginX = trunkEvents[3];

  const branches: { dy: number; events: number[]; label: string }[] = [
    { dy: -150, events: [trunkEvents[3] + 80, trunkEvents[3] + 160, trunkEvents[3] + 240], label: 'Hypothesis A — try plan X' },
    { dy: -80,  events: [trunkEvents[3] + 80, trunkEvents[3] + 160, trunkEvents[3] + 240, trunkEvents[3] + 320], label: 'Hypothesis B — try plan Y' },
    { dy: 110,  events: [trunkEvents[3] + 80, trunkEvents[3] + 160, trunkEvents[3] + 240], label: 'Hypothesis C — try plan Z' },
  ];

  const eventCircle = (x: number, y: number) => (
    <circle cx={x} cy={y} r={9} fill={'#FFFFFF'} stroke={p.accent} strokeWidth={1.5} />
  );

  return (
    <svg className="flow-diagram log" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Hypothesis branches">
      <line x1={60} y1={trunkY} x2={W - 60} y2={trunkY} stroke={p.faint} strokeWidth={1.5} />
      {trunkEvents.map((x, i) => (
        <g key={`t${i}`}>
          {eventCircle(x, trunkY)}
          <text x={x} y={trunkY + 28} textAnchor="middle" fill={p.mute} className="log-tick">v{i + 1}</text>
        </g>
      ))}
      <text x={60} y={trunkY - 28} fill={p.ink} className="log-trunk">Session trunk · append-only, versioned</text>

      {branches.map((b, bi) => {
        const by = trunkY + b.dy;
        return (
          <g key={`b${bi}`}>
            <path
              d={`M ${branchOriginX} ${trunkY} C ${branchOriginX} ${trunkY + b.dy * 0.4}, ${branchOriginX + 40} ${by}, ${branchOriginX + 60} ${by}`}
              stroke={p.accent} strokeWidth={1.5} fill="none" strokeDasharray="4 6"
              className="flow-line" style={{ animationDelay: `${bi * 220}ms` }}
            />
            <line x1={branchOriginX + 60} y1={by} x2={b.events[b.events.length - 1] + 20} y2={by} stroke={p.faint} strokeWidth={1.5} />
            {b.events.map((x, i) => <g key={`b${bi}e${i}`}>{eventCircle(x, by)}</g>)}
            <text x={branchOriginX + 65} y={by - 16} fill={p.accent} className="log-branch">{b.label}</text>
          </g>
        );
      })}

      <text x={W / 2} y={H - 20} textAnchor="middle" fill={p.mute} className="flow-side-label">
        Branching is a pointer copy · agent explores hypotheses cheaply
      </text>
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   ChainDiagram (Replay)
   ══════════════════════════════════════════════════════════════════════ */

export type ChainStep = { label: string; sub?: string; glyph?: Glyph; tone?: 'primary' | 'mute' };
export function ChainDiagram({ steps, tone = 'light' }: { steps: readonly ChainStep[]; tone?: Tone }) {
  const p = pal(tone);
  const W = 1200; const H = 380;
  const cy = H / 2;
  const slotW = W / steps.length;
  const markerId = `arrow-chain-${tone}`;
  return (
    <svg className="flow-diagram chain" viewBox={`0 0 ${W} ${H}`} role="img">
      <defs>
        <marker id={markerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={p.accent} />
        </marker>
      </defs>
      {steps.slice(0, -1).map((_, i) => (
        <path
          key={i}
          d={`M ${slotW * (i + 0.5) + 92} ${cy} L ${slotW * (i + 1.5) - 96} ${cy}`}
          stroke={p.faint} strokeWidth={1.5} strokeDasharray="4 6"
          className="flow-line" style={{ animationDelay: `${i * 200}ms` }}
          markerEnd={`url(#${markerId})`}
        />
      ))}
      {steps.map((s, i) => {
        const x = slotW * (i + 0.5);
        const isPrimary = s.tone === 'primary';
        return (
          <g key={i} transform={`translate(${x - 92}, ${cy - 64})`}>
            <rect width={184} height={128} rx={12} ry={12} fill={p.centerFill} stroke={isPrimary ? p.accent : p.cardBorder} strokeWidth={isPrimary ? 1.6 : 1.1} />
            {s.glyph && (
              <g transform="translate(72, 16)">
                <Glyph kind={s.glyph} size={40} accent={p.accent} />
              </g>
            )}
            <text x={92} y={s.glyph ? 80 : 62} textAnchor="middle" fill={p.ink} className="chain-label">{s.label}</text>
            {s.sub && (
              <text x={92} y={s.glyph ? 102 : 86} textAnchor="middle" fill={p.mute} className="chain-sub">{s.sub}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   HubSpoke (Multi-agent coordination)
   ══════════════════════════════════════════════════════════════════════ */

type Spoke = { label: string; glyph?: Glyph; angle: number; eventLabel?: string };

export function HubSpoke({
  hub, spokes, tone = 'light',
}: { hub: { label: string; sub?: string; glyph?: Glyph }; spokes: readonly Spoke[]; tone?: Tone }) {
  const p = pal(tone);
  const W = 1200; const H = 560;
  const cx = W / 2; const cy = H / 2 - 10;
  const r = 100;
  const ringR = 240;
  const markerId = `arrow-hub-${tone}`;

  return (
    <svg className="flow-diagram hub" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Hub and spoke">
      <defs>
        <marker id={markerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={p.accent} />
        </marker>
      </defs>

      <circle cx={cx} cy={cy} r={r + 12} fill="none" stroke={p.faint} strokeWidth={1} opacity={0.45} />
      <circle cx={cx} cy={cy} r={r} fill={p.centerFill} stroke={p.accent} strokeWidth={1.5} />
      {hub.glyph && (
        <g transform={`translate(${cx - 22}, ${cy - 44})`}>
          <Glyph kind={hub.glyph} size={44} accent={p.accent} />
        </g>
      )}
      <text x={cx} y={cy + (hub.glyph ? 16 : -4)} fill={p.ink} textAnchor="middle" className="flow-center-label">{hub.label}</text>
      {hub.sub && (
        <text x={cx} y={cy + (hub.glyph ? 38 : 18)} fill={p.mute} textAnchor="middle" className="flow-center-sub">
          {hub.sub.split('\n').map((line, i) => (
            <tspan key={i} x={cx} dy={i === 0 ? 0 : 16}>{line}</tspan>
          ))}
        </text>
      )}

      {spokes.map((sp, i) => {
        const rad = (sp.angle * Math.PI) / 180;
        const sx = cx + Math.cos(rad) * ringR;
        const sy = cy + Math.sin(rad) * ringR;

        const lineStartX = cx + Math.cos(rad) * (r + 6);
        const lineStartY = cy + Math.sin(rad) * (r + 6);
        const lineEndX = sx - Math.cos(rad) * 36;
        const lineEndY = sy - Math.sin(rad) * 36;

        const midX = (lineStartX + lineEndX) / 2 + 2;
        const midY = (lineStartY + lineEndY) / 2;

        return (
          <g key={i}>
            <line x1={lineStartX} y1={lineStartY - 4} x2={lineEndX} y2={lineEndY - 4} stroke={p.faint} strokeWidth={1.25} strokeDasharray="3 6" className="flow-line" style={{ animationDelay: `${i * 180}ms` }} markerEnd={`url(#${markerId})`} />
            <line x1={lineEndX} y1={lineEndY + 4} x2={lineStartX} y2={lineStartY + 4} stroke={p.accent} strokeWidth={1.25} strokeDasharray="3 6" className="flow-line" style={{ animationDelay: `${i * 180 + 80}ms` }} markerEnd={`url(#${markerId})`} />
            {sp.eventLabel && (
              <text x={midX} y={midY - 8} fill={p.mute} className="hub-event-label" textAnchor="middle">{sp.eventLabel}</text>
            )}
            <NodeCard
              node={{ label: sp.label, glyph: sp.glyph }}
              x={sx} y={sy}
              side={sx >= cx ? 'right' : 'left'}
              tone={tone}
              large
            />
          </g>
        );
      })}
    </svg>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Internals
   ══════════════════════════════════════════════════════════════════════ */

function NodeCard({
  node, x, y, side, tone, large,
}: {
  node: FlowNode;
  x: number; y: number;
  side: 'left' | 'right';
  tone: Tone;
  large?: boolean;
}) {
  const p = pal(tone);
  const size = large ? 72 : 56;
  const glyphSize = large ? 36 : 32;
  const labelX = side === 'left' ? x + (size / 2 + 14) : x - (size / 2 + 14);
  const textAnchor = side === 'left' ? 'start' : 'end';

  return (
    <g>
      <rect x={x - size / 2} y={y - size / 2} width={size} height={size} rx={10} ry={10}
            fill={p.cardBg} stroke={p.cardBorder} strokeWidth={1} />
      <g transform={`translate(${x - glyphSize / 2}, ${y - glyphSize / 2})`}>
        <Glyph kind={node.glyph ?? 'doc'} size={glyphSize} accent={p.accent} />
      </g>
      <text x={labelX} y={y - 4} textAnchor={textAnchor} fill={p.ink} className="node-label">{node.label}</text>
      {node.sub && (
        <text x={labelX} y={y + 14} textAnchor={textAnchor} fill={p.mute} className="node-sub">{node.sub}</text>
      )}
    </g>
  );
}

function GovernanceArc({
  cx, cy, width, label, stops, tone, pal: passedPal,
}: { cx: number; cy: number; width: number; label: string; stops: readonly string[]; tone: Tone; pal?: ReturnType<typeof pal> }) {
  const p = passedPal ?? pal(tone);
  const r = width / 2;
  const arcY = cy + 30;
  const startX = cx - r;
  const endX = cx + r;
  const arcD = `M ${startX} ${arcY} Q ${cx} ${arcY + 70}, ${endX} ${arcY}`;

  return (
    <g className="gov-arc">
      <path d={arcD} stroke={p.faint} strokeWidth={1.25} fill="none" />
      <text x={cx} y={arcY + 54} fill={p.ink} textAnchor="middle" className="gov-label">{label}</text>
      {stops.map((s, i) => {
        const t = (i + 0.5) / stops.length;
        const x = startX + t * width;
        const dropY = arcY + Math.sin(t * Math.PI) * 70;
        return (
          <g key={s}>
            <rect x={x - 4} y={dropY - 4} width={8} height={8} fill={p.accent} rx={1} />
            <text x={x} y={dropY + 24} textAnchor="middle" fill={p.mute} className="gov-stop">{s}</text>
          </g>
        );
      })}
    </g>
  );
}

/* ── Glyphs ─────────────────────────────────────────────────────────── */

export function Glyph({ kind, size = 32, accent = '#0070D9' }: { kind: Glyph; size?: number; accent?: string }) {
  const ink = '#202939';
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      {glyphPath(kind, ink, accent)}
    </svg>
  );
}

function glyphPath(kind: Glyph, ink: string, accent: string): ReactNode {
  switch (kind) {
    case 'db':
      return (
        <>
          <ellipse cx="16" cy="8" rx="9" ry="3" stroke={ink} strokeWidth="1.5" />
          <path d="M7 8 V20 C7 22 11 24 16 24 C21 24 25 22 25 20 V8" stroke={ink} strokeWidth="1.5" fill="none" />
          <ellipse cx="16" cy="14" rx="9" ry="3" stroke={accent} strokeWidth="1.5" fill="none" />
        </>
      );
    case 'doc':
      return (
        <>
          <path d="M9 5 H20 L25 10 V27 H9 Z" stroke={ink} strokeWidth="1.5" fill="none" />
          <path d="M20 5 V10 H25" stroke={ink} strokeWidth="1.5" />
          <line x1="12" y1="16" x2="22" y2="16" stroke={accent} strokeWidth="1.2" />
          <line x1="12" y1="20" x2="22" y2="20" stroke={accent} strokeWidth="1.2" />
        </>
      );
    case 'vector':
      return (
        <>
          <rect x="5" y="5" width="22" height="22" rx="2" stroke={ink} strokeWidth="1.5" fill="none" />
          {[8, 13, 18, 23].map(y => [8, 13, 18, 23].map(x => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.2" fill={accent} />))}
        </>
      );
    case 'olap':
      return (
        <>
          <rect x="5" y="6" width="22" height="20" rx="2" stroke={ink} strokeWidth="1.5" fill="none" />
          <rect x="9" y="14" width="3" height="9" fill={accent} />
          <rect x="14" y="10" width="3" height="13" fill={accent} />
          <rect x="19" y="17" width="3" height="6" fill={accent} />
        </>
      );
    case 'feature':
      return (
        <>
          <rect x="5" y="5" width="9" height="9" stroke={ink} strokeWidth="1.5" />
          <rect x="18" y="5" width="9" height="9" stroke={ink} strokeWidth="1.5" />
          <rect x="5" y="18" width="9" height="9" stroke={accent} strokeWidth="1.5" fill={accent} fillOpacity="0.2" />
          <rect x="18" y="18" width="9" height="9" stroke={ink} strokeWidth="1.5" />
        </>
      );
    case 'agent':
      return (
        <>
          <rect x="7" y="9" width="18" height="14" rx="3" stroke={ink} strokeWidth="1.5" fill="none" />
          <circle cx="12" cy="16" r="1.5" fill={accent} />
          <circle cx="20" cy="16" r="1.5" fill={accent} />
          <line x1="16" y1="9" x2="16" y2="5" stroke={ink} strokeWidth="1.5" />
          <circle cx="16" cy="4" r="1.5" fill={accent} />
        </>
      );
    case 'tool':
      return (
        <>
          <circle cx="16" cy="16" r="6" stroke={ink} strokeWidth="1.5" fill="none" />
          <path d="M16 7 V4 M16 28 V25 M7 16 H4 M28 16 H25 M9 9 L7 7 M23 23 L25 25 M9 23 L7 25 M23 9 L25 7" stroke={accent} strokeWidth="1.5" />
        </>
      );
    case 'wrench':
      return (
        <>
          <path d="M22 5 a 5 5 0 0 0 -7 7 l -9 9 a 2 2 0 1 0 3 3 l 9 -9 a 5 5 0 0 0 7 -7 l -3 3 l -3 -3 z" stroke={ink} strokeWidth="1.5" fill={accent} fillOpacity="0.15" />
        </>
      );
    case 'gateway':
      return (
        <>
          <path d="M6 8 V24" stroke={ink} strokeWidth="1.5" />
          <path d="M26 8 V24" stroke={ink} strokeWidth="1.5" />
          <rect x="11" y="11" width="10" height="10" rx="2" stroke={accent} strokeWidth="1.5" fill="none" />
          <line x1="7" y1="16" x2="11" y2="16" stroke={accent} strokeWidth="1.2" />
          <line x1="21" y1="16" x2="25" y2="16" stroke={accent} strokeWidth="1.2" />
        </>
      );
    case 'log':
      return (
        <>
          <rect x="6" y="6" width="20" height="20" rx="2" stroke={ink} strokeWidth="1.5" fill="none" />
          <line x1="9" y1="11" x2="23" y2="11" stroke={accent} strokeWidth="1.2" />
          <line x1="9" y1="15" x2="20" y2="15" stroke={accent} strokeWidth="1.2" />
          <line x1="9" y1="19" x2="22" y2="19" stroke={accent} strokeWidth="1.2" />
          <line x1="9" y1="23" x2="18" y2="23" stroke={accent} strokeWidth="1.2" />
        </>
      );
    case 'branch':
      return (
        <>
          <circle cx="9" cy="9" r="3" stroke={ink} strokeWidth="1.5" />
          <circle cx="9" cy="24" r="3" stroke={ink} strokeWidth="1.5" />
          <circle cx="24" cy="9" r="3" stroke={accent} strokeWidth="1.5" />
          <line x1="9" y1="12" x2="9" y2="21" stroke={ink} strokeWidth="1.5" />
          <path d="M9 14 C 9 9, 16 9, 24 9" stroke={accent} strokeWidth="1.5" fill="none" />
        </>
      );
    case 'rules':
      return (
        <>
          <rect x="5" y="5" width="22" height="22" rx="2" stroke={ink} strokeWidth="1.5" fill="none" />
          <text x="9" y="14" fontFamily="ui-monospace" fontSize="6" fill={accent}>if {'>'}</text>
          <text x="9" y="22" fontFamily="ui-monospace" fontSize="6" fill={accent}>do x</text>
        </>
      );
    case 'cache':
      return (
        <>
          <circle cx="16" cy="16" r="11" stroke={ink} strokeWidth="1.5" fill="none" />
          <path d="M16 7 V16 L22 19" stroke={accent} strokeWidth="1.5" />
        </>
      );
    case 'router':
      return (
        <>
          <rect x="8" y="13" width="16" height="6" rx="2" stroke={ink} strokeWidth="1.5" fill="none" />
          <circle cx="12" cy="16" r="1" fill={accent} />
          <circle cx="16" cy="16" r="1" fill={accent} />
          <circle cx="20" cy="16" r="1" fill={accent} />
          <line x1="16" y1="13" x2="16" y2="5" stroke={accent} strokeWidth="1.5" />
          <line x1="8" y1="19" x2="6" y2="27" stroke={accent} strokeWidth="1.5" />
          <line x1="24" y1="19" x2="26" y2="27" stroke={accent} strokeWidth="1.5" />
        </>
      );
    case 'audit':
      return (
        <>
          <rect x="7" y="5" width="18" height="22" rx="2" stroke={ink} strokeWidth="1.5" fill="none" />
          <circle cx="20" cy="22" r="3" stroke={accent} strokeWidth="1.5" />
          <line x1="22" y1="24" x2="24" y2="26" stroke={accent} strokeWidth="1.5" />
          <line x1="10" y1="11" x2="20" y2="11" stroke={ink} strokeWidth="1" />
          <line x1="10" y1="15" x2="18" y2="15" stroke={ink} strokeWidth="1" />
        </>
      );
    case 'replay':
      return (
        <>
          <path d="M9 12 A8 8 0 1 1 9 20" stroke={ink} strokeWidth="1.5" fill="none" />
          <polygon points="9,9 12,12 6,12" fill={accent} />
          <polygon points="13,16 19,12 19,20" fill={accent} />
        </>
      );
    case 'mcp':
      return (
        <>
          <rect x="6" y="10" width="20" height="12" rx="3" stroke={ink} strokeWidth="1.5" fill="none" />
          <text x="16" y="19" textAnchor="middle" fontFamily="ui-monospace" fontWeight="600" fontSize="7" fill={accent}>MCP</text>
        </>
      );
    case 'image':
      return (
        <>
          <rect x="5" y="7" width="22" height="18" rx="2" stroke={ink} strokeWidth="1.5" fill="none" />
          <circle cx="11" cy="13" r="2" fill={accent} />
          <path d="M5 23 L13 17 L20 22 L27 16 V25 H5 Z" fill={accent} fillOpacity="0.3" stroke="none" />
        </>
      );
    case 'audio':
      return (
        <>
          {[8, 12, 16, 20, 24].map((x, i) => {
            const h = [10, 18, 8, 16, 12][i];
            return <rect key={x} x={x} y={16 - h / 2} width="2" height={h} fill={accent} />;
          })}
        </>
      );
    case 'crm':
      return (
        <>
          <circle cx="11" cy="13" r="3" stroke={ink} strokeWidth="1.5" fill="none" />
          <circle cx="21" cy="13" r="3" stroke={ink} strokeWidth="1.5" fill="none" />
          <path d="M5 23 Q11 18 17 23" stroke={accent} strokeWidth="1.5" fill="none" />
          <path d="M15 23 Q21 18 27 23" stroke={accent} strokeWidth="1.5" fill="none" />
        </>
      );
    case 'lake':
      return (
        <>
          <path d="M4 14 Q10 11 16 14 T28 14" stroke={accent} strokeWidth="1.5" fill="none" />
          <path d="M4 19 Q10 16 16 19 T28 19" stroke={accent} strokeWidth="1.5" fill="none" />
          <path d="M4 24 Q10 21 16 24 T28 24" stroke={ink} strokeWidth="1.5" fill="none" />
        </>
      );
    case 'stream':
      return (
        <>
          <path d="M4 10 Q10 7 16 10 T28 10" stroke={accent} strokeWidth="1.5" fill="none" />
          <path d="M4 16 Q10 13 16 16 T28 16" stroke={ink} strokeWidth="1.5" fill="none" />
          <path d="M4 22 Q10 19 16 22 T28 22" stroke={accent} strokeWidth="1.5" fill="none" />
        </>
      );
    case 'llm':
      return (
        <>
          <circle cx="16" cy="16" r="11" stroke={ink} strokeWidth="1.5" fill="none" />
          <text x="16" y="20" textAnchor="middle" fontFamily="ui-monospace" fontWeight="700" fontSize="8" fill={accent}>LLM</text>
        </>
      );
    case 'function':
      return (
        <>
          <rect x="5" y="6" width="22" height="20" rx="3" stroke={ink} strokeWidth="1.5" fill="none" />
          <text x="16" y="20" textAnchor="middle" fontFamily="ui-monospace" fontWeight="700" fontSize="11" fill={accent}>{`{ }`}</text>
        </>
      );
    case 'flask':
      return (
        <>
          <path d="M12 4 V12 L7 24 H25 L20 12 V4" stroke={ink} strokeWidth="1.5" fill="none" />
          <line x1="11" y1="4" x2="21" y2="4" stroke={ink} strokeWidth="1.5" />
          <circle cx="14" cy="20" r="1.5" fill={accent} />
          <circle cx="18" cy="22" r="1.5" fill={accent} />
        </>
      );
    default:
      return <rect x="5" y="5" width="22" height="22" rx="3" fill={accent} fillOpacity="0.2" stroke={ink} strokeWidth="1.5" />;
  }
}
